import styles from './index.module.scss';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Typography } from '@navi/web-ui/lib/primitives';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { setSelectedCycle } from '@cp/src/reducers/allocationSlice';
import Dropdown from '@cp/src/components/Dropdown';
import { SelectPickerOptionProps } from '@cp/src/components/Dropdown/interfaces';
import InProgressFooter from './footer/InProgressFooter';
import { getAllocationStatus, getAllocationSummary } from './action';
import { DateFormat, formatDate } from '@cp/src/utils/DateHelper';
import SuccessFooter from './footer/SuccessFooter';
import FailedFooter from './footer/FailedFooter';
import SuccessAllocationDetails from './SuccessAllocationDetails';
import FailedAllocationDetails from './FailedAllocationDetails';
import { CollectionType, FAILURE_REASON } from './consts';
import { ALLOCATION_STATUS } from './interface';
import { convertObjectToDropdownOptions, getDecodedUrlParam, findOptionByValue } from './utils';

const SystemAllocationStatus = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedCycle = useSelector((state: RootState) => state.allocations.selectedCycle);
  const configData = useSelector((state: RootState) => state.allocations.configData);

  useEffect(() => {
    dispatch(getAllocationStatus(CollectionType.TELE));
  }, []);

  const summaryData = useSelector((state: RootState) => state.allocations?.summaryData);

  const cycleOptions = useMemo(
    () => convertObjectToDropdownOptions(configData as Record<string, string>),
    [configData]
  );

  const [searchParams] = useSearchParams();
  const urlCycle = searchParams.get('cycle');
  const decodedUrlCycle = useMemo(() => getDecodedUrlParam(urlCycle), [urlCycle]);

  const defaultSelectedOption = useMemo(
    () => findOptionByValue(cycleOptions, decodedUrlCycle),
    [cycleOptions, decodedUrlCycle]
  );

  const handleOnChange = (selectedOption: SelectPickerOptionProps) => {
    const cycleValue = selectedOption?.value as string;
    dispatch(setSelectedCycle(cycleValue));
    const params = new URLSearchParams({ cycle: cycleValue });
    navigate(`?${params.toString()}`);
  };

  useEffect(() => {
    if (decodedUrlCycle && !selectedCycle) {
      dispatch(setSelectedCycle(decodedUrlCycle));
    }
  }, [decodedUrlCycle, selectedCycle]);

  useEffect(() => {
    if (selectedCycle) {
      dispatch(getAllocationSummary(selectedCycle, CollectionType.TELE));
    }
  }, [selectedCycle]);

  const renderAllocationTypeInfo = () => {
    if (summaryData?.allocationStatus === ALLOCATION_STATUS.COMPLETED) {
      return (
        <div className="flex justify-between gap-4">
          <Typography variant="h4" color="var(--navi-color-gray-c3)">
            Allocation type:
          </Typography>
          <Typography variant="h4" color="var(--navi-color-gray-c2)">
            {summaryData?.allocationRunType}
          </Typography>
        </div>
      );
    }
    return null;
  };

  const renderFooter = () => {
    switch (summaryData?.allocationStatus) {
      case ALLOCATION_STATUS.INPROGRESS:
        return <InProgressFooter />;
      case ALLOCATION_STATUS.FAILED:
        return <FailedFooter />;
      case ALLOCATION_STATUS.COMPLETED:
        return <SuccessFooter />;
      default:
        return null;
    }
  };

  const renderDetailsSection = () => {
    const status = summaryData?.allocationStatus;
    const reason = summaryData?.failureMeta?.reason;

    const isCapacityBreach = reason === FAILURE_REASON.CAPACITY_BREACH;
    const isFailedAndCapacityBreach = status === ALLOCATION_STATUS.FAILED && isCapacityBreach;
    const isCompleted = status === ALLOCATION_STATUS.COMPLETED;

    if (isFailedAndCapacityBreach) {
      return <FailedAllocationDetails />;
    }

    if (isCompleted) {
      return <SuccessAllocationDetails />;
    }

    return null;
  };

  const handleOnClick = () => {
    navigate(-1);
  };

  return (
    <>
      <div className={styles.topNavContainer}>
        <div className="cursor-pointer flex flex-row gap-4 w-[300px]" onClick={handleOnClick}>
          <BackIcon size={'16'} />
          <Typography variant={'h2'} color="var(--navi-color-gray-c2)">
            System Allocation Status
          </Typography>
        </div>
      </div>

      <Card className={styles.midContainer}>
        <div>
          <div className="flex flex-row mt-6 mx-6 justify-between items-start">
            <div className="mb-4">
              <Typography variant="h4" className="mb-2" color="var(--navi-color-gray-c3)">
                Select cycle
              </Typography>
              <Dropdown
                options={cycleOptions}
                selectedValue={selectedCycle}
                defaultSelected={defaultSelectedOption}
                onSelectionChange={handleOnChange}
                containerClasses={'w-[200px]'}
                pickerContainerClasses={'!z-[var(--z-index-leaderboard)]'}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between gap-4">
                <Typography variant="h4" color="var(--navi-color-gray-c3)">
                  Allocation initiated on :
                </Typography>
                <Typography variant="h4" color="var(--navi-color-gray-c2)">
                  {formatDate(
                    summaryData?.allocationInitiatedAt as string,
                    DateFormat.DD_MMM_YYYY__hh_mm_a
                  )}
                </Typography>
              </div>

              {renderAllocationTypeInfo()}
            </div>
          </div>
          {renderFooter()}
        </div>
      </Card>

      {renderDetailsSection()}
    </>
  );
};

export default SystemAllocationStatus;
